
import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import PuttingHistoryTabs from "@/components/putting/training/PuttingHistoryTabs";
import { SwingTendency } from '@/components/putting/training/charts/swing/types';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DriverHistorySectionProps {
  tendencies: SwingTendency[];
  onDeleteTendency: (id: string) => Promise<void>;
  onLevelChange: (level: string | null) => void;
}

/**
 * Component that displays driver training history by level
 */
const DriverHistorySection: React.FC<DriverHistorySectionProps> = ({ 
  tendencies, 
  onDeleteTendency,
  onLevelChange
}) => {
  const { t } = useTranslation('training');
  // State to keep track of the current filter level
  const [currentLevel, setCurrentLevel] = useState<string | null>(null);
  
  // Group and deduplicate tendencies by date and level - this fixes the duplicate history issue
  const groupTendenciesByDateAndLevel = (tendencies: SwingTendency[]): SwingTendency[] => {
    const uniqueEntries = new Map<string, SwingTendency>();
    
    // Sort tendencies by date (newest first)
    const sortedTendencies = [...tendencies].sort((a, b) => 
      new Date(b.test_date).getTime() - new Date(a.test_date).getTime()
    );
    
    // First pass: collect all tendencies with level information
    for (const tendency of sortedTendencies) {
      if (tendency.test_level && tendency.test_level.trim() !== '') {
        const date = new Date(tendency.test_date).toISOString().split('T')[0]; // Get just the date part
        const level = tendency.test_level;
        const key = `${date}-${level}`;
        
        if (!uniqueEntries.has(key) || 
            (tendency.points && uniqueEntries.get(key)?.points !== undefined && 
             tendency.points > uniqueEntries.get(key)!.points!)) {
          uniqueEntries.set(key, tendency);
        }
      }
    }
    
    // Second pass: only add standard (no level) entries if there's no level entry for that date
    // AND only if explicitly asking for standard entries (currentLevel is null)
    if (currentLevel === null) {
      for (const tendency of sortedTendencies) {
        if (!tendency.test_level || tendency.test_level.trim() === '') {
          const date = new Date(tendency.test_date).toISOString().split('T')[0]; // Get just the date part
          
          // Check if we already have any level entries for this date
          let hasLevelEntryForDate = false;
          for (const [key, entry] of uniqueEntries.entries()) {
            if (key.startsWith(date)) {
              hasLevelEntryForDate = true;
              break;
            }
          }
          
          // Only add the standard entry if there's no level entry for this date
          if (!hasLevelEntryForDate) {
            const key = `${date}-standard`;
            if (!uniqueEntries.has(key) || 
                (tendency.points && uniqueEntries.get(key)?.points !== undefined && 
                tendency.points > uniqueEntries.get(key)!.points!)) {
              uniqueEntries.set(key, tendency);
            }
          }
        }
      }
    }
    
    return Array.from(uniqueEntries.values());
  };

  // Filter only driver tendencies (make sure we only have driver tendencies)
  const allDriverTendencies = tendencies.filter(t => 
    t.club_type === "driver" || 
    t.test_id === "driver-precision" || 
    (t.test_id && t.test_id.includes("driver"))
  );
  
  // Group tendencies by level
  const level1Tendencies = allDriverTendencies.filter(t => t.test_level === "1");
  const level2Tendencies = allDriverTendencies.filter(t => t.test_level === "2");
  const level3Tendencies = allDriverTendencies.filter(t => t.test_level === "3");

  // Get grouped and deduplicated tendencies for history
  const uniqueDriverTendencies = groupTendenciesByDateAndLevel(allDriverTendencies);
  const uniqueLevel1Tendencies = groupTendenciesByDateAndLevel(level1Tendencies);
  const uniqueLevel2Tendencies = groupTendenciesByDateAndLevel(level2Tendencies);
  const uniqueLevel3Tendencies = groupTendenciesByDateAndLevel(level3Tendencies);

  // Handle level change properly
  const handleLevelChange = (level: string | null) => {
    console.log("DriverHistorySection: Level changed to:", level);
    setCurrentLevel(level);
    if (onLevelChange) {
      onLevelChange(level);
    }
  };

  // Convert tendency to history item format
  const convertTendencyToHistoryItem = (t: SwingTendency) => ({
    id: t.id,
    user_id: t.user_id,
    test_date: t.test_date,
    test_level: t.test_level, // Use the level value directly
    test_name: t.test_name || "Driver - Précision sur la mise en jeu",
    test_id: t.test_id || "driver_accuracy",
    points: typeof t.points === 'number' ? t.points : 0,
    situation_id: t.situation_id || 0,
    index: t.index || 0,
    metadata: {
      tendency: t.tendency
    }
  });

  // Handle delete properly
  const handleDelete = async (id: string) => {
    console.log("DriverHistorySection: Delete requested for ID:", id);
    if (onDeleteTendency) {
      try {
        await onDeleteTendency(id);
      } catch (error) {
        console.error("Error in DriverHistorySection handleDelete:", error);
        toast.error(t('toasts.errorDeleting.description'));
      }
    }
  };

  // Debug logs
  useEffect(() => {
    console.log(`DriverHistorySection: Current level filter: ${currentLevel}`);
    console.log(`All driver tendencies: ${allDriverTendencies.length}`);
    console.log(`Level 1 tendencies: ${level1Tendencies.length}`);
    console.log(`Level 2 tendencies: ${level2Tendencies.length}`);
    console.log(`Level 3 tendencies: ${level3Tendencies.length}`);
    console.log(`Unique driver tendencies after grouping: ${uniqueDriverTendencies.length}`);
  }, [currentLevel, allDriverTendencies.length]);

  return (
    <div className="mt-12">
      <h2 className="text-xl font-bold mb-6 text-gray-800">{t('history.driverPrecision')}</h2>
      <PuttingHistoryTabs
        puttingResults={uniqueDriverTendencies.map(convertTendencyToHistoryItem)}
        level1Results={uniqueLevel1Tendencies.map(convertTendencyToHistoryItem)}
        level2Results={uniqueLevel2Tendencies.map(convertTendencyToHistoryItem)}
        level3Results={uniqueLevel3Tendencies.map(convertTendencyToHistoryItem)}
        onLevelChange={handleLevelChange}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default DriverHistorySection;
